import * as errors from '@attain-apps/errors'
import * as store from '@attain-apps/fe/web/main/lib/store'
import { uncapitalize } from '@attain-apps/string-utils'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import type { Collection } from '../../collection/collection'
import type * as models from '../../models'
import { Plugin } from '../plugin'
import * as standard from '../standard'

import { getActionsConfig } from './actions'
import type { Config } from './config'
import { Selectors } from './selectors'
import { State } from './state'

const INIT_EVENT = 'ec.plugins.Manager.init()'

/**
 * Manages all of the {@link Plugin} on a {@link Collection}
 */
export class Manager<TTypeParams extends models.TypeParams.Constraint>
  implements StandardPluginReferences<TTypeParams>
{
  /**
   * All {@link Plugin} tracked by this {@link Manager}
   */
  public get all(): readonly Plugin.Default<TTypeParams, Plugin.Id>[] {
    return Array.from(this._lookup.values())
  }

  /**
   * Final list of plugin factories, including
   * {@link _getBuiltinPluginFactories} and {@link Config.additionalFactories}
   */
  private readonly _factories: readonly Plugin.Factory<
    TTypeParams,
    Plugin.Id,
    Plugin.Constraint<TTypeParams>
  >[]

  /**
   * Map of plugins registered with this entity collection
   */
  private readonly _lookup = new Map<
    Plugin.Id,
    Plugin.Constraint<TTypeParams, Plugin.Id>
  >()
  /** @see {@link standard.apiExecution.Plugin} */
  declare public readonly apiExecutionPlugin: standard.apiExecution.Plugin<TTypeParams>
  /** @see {@link standard.attribute.Plugin} */
  declare public readonly attributePlugin: standard.attribute.Plugin<TTypeParams>
  /** @see {@link standard.cache.Plugin} */
  declare public readonly cachePlugin: standard.cache.Plugin<TTypeParams>
  /** @see {@link standard.currentEntity.Plugin} */
  declare public readonly currentEntityPlugin: standard.currentEntity.Plugin<TTypeParams>
  /** @see {@link standard.currentList.Plugin} */
  declare public readonly currentListPlugin: standard.currentList.Plugin<TTypeParams>
  /** @see {@link standard.dataOperation.Plugin} */
  declare public readonly dataOperationPlugin: standard.dataOperation.Plugin<TTypeParams>
  /** @see {@link standard.editing.Plugin} */
  declare public readonly editingPlugin: standard.editing.Plugin<TTypeParams>
  /** @see {@link standard.group.Plugin} */
  declare public readonly groupPlugin: standard.group.Plugin<TTypeParams>
  /** @see {@link standard.selection.Plugin} */
  declare public readonly selectionPlugin: standard.selection.Plugin<TTypeParams>

  /**
   * TODO:
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly feature

  constructor(
    private readonly _collection: Collection<TTypeParams>,
    config: Config<TTypeParams> = {},
  ) {
    for (const standardPluginId of Plugin.Id.Standard.ALL) {
      // Adds getters for every standard plugin
      const key =
        `${uncapitalize(standardPluginId)}Plugin` satisfies keyof StandardPluginReferences<TTypeParams>
      Object.defineProperty(this, key, {
        // Just uses lookup to do the actually getting
        get: () =>
          this.lookup(
            standardPluginId,
          ).get() satisfies StandardPluginReferences<TTypeParams>[typeof key],
      })
    }

    this.feature = new store.Feature({
      actions: getActionsConfig(),
      initialState: State.INITIAL,
      name: 'Plugins',
      parent: _collection.feature,
      selectors: () => new Selectors(this),
    })

    this._factories = [
      ..._getBuiltinPluginFactories(config),
      ...(config.additionalFactories ?? []),
    ]
  }

  /**
   * TODO: explain
   *
   * Allows entity collection to initialize plugins on command
   */
  public init() {
    for (const pluginFactory of this._factories) {
      // NOTE: explicit type avoids a lot of implicit `any` type parameters
      const plugin:
        | Plugin<TTypeParams, Plugin.Id, unknown, unknown, any>
        | undefined = pluginFactory.produceFor(this._collection)
      if (!plugin) {
        // If no plugin was produced, we can skip to the next factory
        continue
      }
      if (this._lookup.has(plugin.id)) {
        // If plugin already exists, we have an issue
        throw new errors.AlreadyExistsError({
          id: plugin.id,
          location: `in ${this._collection.name} entity collection`,
          type: 'plugin',
        })
      }
      this._lookup.set(plugin.id, plugin)
    }
    // All plugins have been instantiated
    this.feature.actions.setPluginsStatus
      .from(INIT_EVENT)
      .dispatch({ pluginsStatus: 'instantiated' })

    // Effects shouldn't be registered until all plugins have been
    // instantiated, to avoid cases where actions are dispatched before
    // reducers are registered and issues in plugins referencing each other in
    // the effects
    for (const plugin of this.all) {
      // NOTE: we assume plugins effects were disabled from the start
      plugin.feature.initEffects()
    }
    // All plugins have been initialized
    this.feature.actions.setPluginsStatus
      .from(INIT_EVENT)
      .dispatch({ pluginsStatus: 'initialized' })
  }

  /**
   * Can be used to retrieve a desired {@link Plugin} by passing the ID or the
   * class/constructor of the desired {@link Plugin}. Returns a
   * {@link Manager.Entry} that has various methods to actually return the
   * {@link Plugin}
   */
  public lookup<const TId extends Plugin.Id.Standard>(
    id: TId,
  ): Manager.Entry<standard.Of<TId, TTypeParams>>
  public lookup<const TId extends Plugin.Id>(
    id: TId,
  ): Manager.Entry<Plugin.Default<TTypeParams, TId>>
  public lookup<const TPlugin extends Plugin.Constraint<TTypeParams>>(
    expectedClass: new (...args: any) => TPlugin,
  ): Manager.Entry<TPlugin>
  public lookup<const TPlugin extends Plugin.Constraint>(
    expectedClass: new (...args: any) => TPlugin,
  ): Manager.Entry<TPlugin>
  public lookup(
    arg: Plugin.Id | (new (...args: any) => Plugin.Constraint<TTypeParams>),
  ): Manager.Entry<Plugin.Default<TTypeParams>> {
    /**
     * Allows the methods below to reference each other (by not being arrow
     * functions), while also having access to this {@link Manager}
     */
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _this = this
    return {
      get() {
        const plugin = this.tryGet()
        if (!plugin) {
          throw new errors.NotFoundError({
            id: typeof arg === 'function' ? arg.name : arg,
            type: 'plugin',
          })
        }
        return plugin
      },
      getWhenReady$() {
        return this.tryGetWhenReady$().pipe(
          // We only want defined values
          rxjs.filter(TypeChecker.isNotNullish.raw),
        )
      },
      tryGet() {
        if (typeof arg === 'function') {
          // If this is a constructor (which in JavaScript, is a function), we
          // look for an instance of that constructor
          return _this.all.find(plugin => plugin instanceof arg)
        }
        // Otherwise, we lookup the plugin by id
        return _this._lookup.get(arg)
      },
      tryGetWhenReady$() {
        return _this.feature.selectors.allPluginsAreReady.asObservable.pipe(
          // We wait until all plugins are ready
          rxjs.filter(isReady => isReady),
          rxjs.take(1),
          // Then we return the requested plugin
          rxjs.map(() => this.tryGet()),
        )
      },
    }
  }
}
export namespace Manager {
  export interface Entry<TPlugin extends Plugin.Constraint> {
    /**
     * TODO:
     */
    get(): TPlugin
    /**
     * TODO:
     */
    tryGet(): TPlugin | undefined
    /**
     * TODO:
     */
    getWhenReady$(): rxjs.Observable<TPlugin>
    /**
     * TODO:
     */
    tryGetWhenReady$(): rxjs.Observable<TPlugin | undefined>
  }
}

type StandardPluginReferences<
  TTypeParams extends models.TypeParams.Constraint,
> = {
  [TId in Plugin.Id.Standard as `${Uncapitalize<TId>}Plugin`]: standard.Of<
    TId,
    TTypeParams
  >
}

/**
 * Helper function to get built-in plugin factories
 */
function _getBuiltinPluginFactories<TTypeParams extends models.TypeParams>(
  config: Config<TTypeParams>,
): Manager<TTypeParams>['_factories'] {
  // NOTE: order may matter (not sure)
  return [
    new standard.cache.Factory<TTypeParams>(config.cache),
    new standard.apiExecution.Factory<TTypeParams>(),
    new standard.attribute.Factory<TTypeParams>(),
    new standard.dataOperation.Factory<TTypeParams>(config.dataOperation),
    new standard.currentEntity.Factory<TTypeParams>(config.currentEntity),
    new standard.editing.Factory<TTypeParams>(config.editing),
    new standard.selection.Factory<TTypeParams>(config.selection),
    new standard.group.Factory<TTypeParams>(config.group),
    new standard.currentList.Factory<TTypeParams>(config.currentList),
  ]
}
